import type { LeadFormProps } from "../../types/formTypes";
import DoneCard from "./DoneCard";
import LeadFormHeader from "./LeadFormHeader";
import StepIndicators from "./StepIndicators";
import DatePickerStep from "./DatePickerStep";
import TimePickerStep from "./TimePickerStep";
import InfoStep from "./InfoStep";
import ServicesStep from "./ServicesStep";
import TradeInStep from "./TradeInStep";
import { useLeadFormState } from "../../hooks/useLeadFormState";

const LeadForm = ({
  formActive,
  onBooked,
  clearAttachedCar,
  scrollToBottom,
  setFormActive,
  refreshSession,
}: LeadFormProps) => {
  const s = useLeadFormState({
    formActive,
    onBooked,
    scrollToBottom,
    clearAttachedCar,
    setFormActive,
    refreshSession,
  });
  if (s.step === "done") {
    return (
      <DoneCard
        isTestDriveFlow={s.isTestDriveFlow}
        vehicleLabel={formActive?.carName || ""}
        selectedDate={s.selectedDate}
        selectedTime={s.selectedTime}
        formatDate={s.formatDate}
        formatSlot2h={s.formatSlot2h}
        showEstimateTradeInButton={s.showEstimateTradeInButton}
        onEstimateTradeIn={s.goToTradeInStep}
      />
    );
  }

  return (
    <div className="tdb-root">
      <LeadFormHeader
        isTestDriveFlow={s.isTestDriveFlow}
        isTradeIn={s.step === "tradein"}
        label={formActive?.label || ""}
        clearAttachedCar={clearAttachedCar}
      />

      <div className="tdb-body">
        <StepIndicators steps={s.steps} step={s.step} stepLabel={s.stepLabel} />

        {s.step === "tradein" && (
          <TradeInStep
            plateNumber={s.plateNumber}
            mileage={s.mileage}
            hasCtDefects={s.hasCtDefects}
            isFirstOwner={s.isFirstOwner}
            isImported={s.isImported}
            isMaintenanceUpToDate={s.isMaintenanceUpToDate}
            tradeInMode={s.tradeInMode}
            isAlreadyOnline={s.isAlreadyOnline}
            setPlateNumber={s.setPlateNumber}
            setMileage={s.setMileage}
            setHasCtDefects={s.setHasCtDefects}
            setIsFirstOwner={s.setIsFirstOwner}
            setIsImported={s.setIsImported}
            setIsMaintenanceUpToDate={s.setIsMaintenanceUpToDate}
            setTradeInMode={s.setTradeInMode}
            setIsAlreadyOnline={s.setIsAlreadyOnline}
            onContinue={s.goToNextAfterTradeIn}
            onBack={s.canBackFromTradeIn ? s.goBackFromTradeIn : undefined}
          />
        )}

        {s.isTestDriveFlow && s.step === "date" && (
          <DatePickerStep
            currentMonth={s.currentMonth}
            firstDay={s.firstDay}
            daysInMonth={s.daysInMonth}
            prevMonth={s.prevMonth}
            nextMonth={s.nextMonth}
            isDateSelectable={s.isDateSelectable}
            selectedDate={s.selectedDate}
            onSelectDate={s.onSelectDate}
          />
        )}

        {s.isTestDriveFlow && s.step === "time" && s.selectedDate && (
          <TimePickerStep
            selectedDate={s.selectedDate}
            selectedTime={s.selectedTime}
            availableSlots={s.availableSlots}
            formatDate={s.formatDate}
            formatSlot2h={s.formatSlot2h}
            onSelectTime={s.onSelectTime}
            onBackToDate={() => s.setStep("date")}
          />
        )}

        {s.step === "info" && (
          <InfoStep
            isTestDriveFlow={s.isTestDriveFlow}
            selectedDate={s.selectedDate}
            selectedTime={s.selectedTime}
            formatDate={s.formatDate}
            formatSlot2h={s.formatSlot2h}
            name={s.name}
            phone={s.phone}
            email={s.email}
            setName={s.setName}
            setPhone={s.setPhone}
            setEmail={s.setEmail}
            hasServiceQuestions={s.hasServiceQuestions}
            onContinue={s.goToNextAfterInfo}
            onBackToTime={
              s.canBackToTimeFromInfo ? () => s.setStep("time") : undefined
            }
          />
        )}

        {s.step === "services" && s.hasServiceQuestions && (
          <ServicesStep
            needsFinancingQuestion={s.needsFinancingQuestion}
            needsTradeInQuestion={s.needsTradeInQuestion}
            wantsFinancing={s.wantsFinancing}
            hasTradeIn={s.hasTradeIn}
            setWantsFinancing={s.setWantsFinancing}
            setHasTradeIn={s.setHasTradeIn}
            onSubmit={s.handleSubmit}
            onBack={() => s.setStep("info")}
          />
        )}
      </div>
    </div>
  );
};

export default LeadForm;
